import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { Dashboard, Products, Categories, Customers, Orders, NewOrder, Sellers, Roles, Expenses, Income, Settings, Login, Register, CreateShop, Profile, Subscription, Privacy } from './pages';
import { ErrorBoundary } from './miniapp/components/ErrorBoundary';
import { useAuthStore } from './store/auth';
import { initTelegramChrome } from './app/surface';
import { TelegramBind } from './features/auth/TelegramBind';
import { RequirePermission } from './app/RequirePermission';
import { ToastProvider } from './components/ui/ToastProvider';
import { ConfirmProvider } from './components/ui/ConfirmProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Tasdiqlangan, ammo hali do'koni yo'q sotuvchini /create-shop ga majburlash.
const sellerNeedsShop = (seller: ReturnType<typeof useAuthStore.getState>['seller'], isAdmin: boolean) =>
  seller.is_seller && !isAdmin && seller.status === 'approved' && !seller.has_shop;

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    isAuthenticated, isLoading, checkAuth, seller, permissions, needsTelegramBind,
  } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Telegram akkaunt hech qaysi hisobga bog'lanmagan: login sahifasi bu yerda
  // yordam bermaydi, parolni bir marta so'raymiz va akkauntni bog'laymiz.
  if (needsTelegramBind) {
    return <TelegramBind onBound={() => window.location.reload()} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (sellerNeedsShop(seller, permissions.is_admin)) {
    return <Navigate to="/create-shop" replace />;
  }

  // Obuna tugagan: faqat Profil (Obuna, Chiqish) ochiq. Server ham bloklaydi —
  // bu yo'naltirish foydalanuvchi bo'sh/xato ekranlarni ko'rmasligi uchun.
  if (seller.subscription && !seller.subscription.active && !location.pathname.startsWith('/profile')) {
    return <Navigate to="/profile/obuna" replace />;
  }

  return <>{children}</>;
};

// /create-shop uchun: autentifikatsiya kerak, lekin do'koni bor bo'lsa panelga qaytaramiz.
const CreateShopRoute: React.FC = () => {
  const { isAuthenticated, isLoading, seller, permissions, checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!sellerNeedsShop(seller, permissions.is_admin)) {
    return <Navigate to="/" replace />;
  }
  return <CreateShop />;
};

function App() {
  // Auth tekshiruvi faqat ProtectedRoute ichida — bu yerda takror chaqirmaymiz
  // (aks holda sahifa ikki marta yuklangandek "flash" bo'ladi).

  // Telegram oynasini to'liq ochamiz. Ilgari bu yerda alohida yo'l bor edi:
  // Telegram'da butunlay boshqa ekranlar (`MiniApp`) chiqardi. Endi bitta
  // ilova — Telegram ham, brauzer ham bir xil ekranlarni ko'rsatadi va faqat
  // ekran kengligiga qarab moslashadi.
  useEffect(() => { initTelegramChrome(); }, []);

  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* Do'kon yaratish (autentifikatsiya kerak, Layout'siz) */}
              <Route path="/create-shop" element={<CreateShopRoute />} />

              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      {/* Ruxsat qo'riqchisi Layout ICHIDA — navigatsiya ko'rinib
                          tursin, foydalanuvchi boshqa bo'limga o'ta olsin. */}
                      <RequirePermission>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/products" element={<Products />} />
                        <Route path="/categories" element={<Categories />} />
                        <Route path="/customers" element={<Customers />} />
                        <Route path="/orders" element={<Orders />} />
                        <Route path="/orders/new" element={<NewOrder />} />
                        <Route path="/sellers" element={<Sellers />} />
                        <Route path="/roles" element={<Roles />} />
                        <Route path="/expenses" element={<Expenses />} />
                        <Route path="/income" element={<Income />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/profile/obuna" element={<Subscription />} />
                        {/* Eski havola — Telegram menyusida saqlanib qolgan bo'lishi mumkin. */}
                        <Route path="/miniapp" element={<Navigate to="/orders/new" replace />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      </RequirePermission>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
